import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { FiArrowLeft, FiCheckCircle, FiMail } from "react-icons/fi";

import { API_URL } from "../config";

function Login() {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [serverStatus, setServerStatus] = useState("checking");
	const { login } = useAuth();
	const navigate = useNavigate();

	// Forgot password state
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [resetError, setResetError] = useState("");
	const [resetSuccess, setResetSuccess] = useState("");
	const [resetLoading, setResetLoading] = useState(false);

	useEffect(() => {
		const checkServer = async () => {
			try {
				const response = await axios.get(`${API_URL}/test`, { timeout: 2000 });
				if (response.data && response.data.message) {
					setServerStatus("connected");
				} else {
					setServerStatus("disconnected");
				}
			} catch (err) {
				console.log("Server check failed:", err.message);
				setServerStatus("disconnected");
			}
		};
		checkServer();
		const interval = setInterval(checkServer, 3000);
		return () => clearInterval(interval);
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		const result = await login(username, password);
		setLoading(false);

		if (result.success) {
			navigate("/dashboard");
		} else {
			setError(result.error);
		}
	};

	const handleRequestReset = async (e) => {
		e.preventDefault();
		setResetError("");
		setResetSuccess("");
		setResetLoading(true);

		try {
			const response = await axios.post(`${API_URL}/auth/request-reset`, {
				email: resetEmail,
			});
			setResetSuccess(response.data.message);
			setResetEmail("");
		} catch (err) {
			setResetError(err.response?.data?.error || "Error sending reset email");
		}

		setResetLoading(false);
	};

	const handleBackToLogin = () => {
		setShowForgotPassword(false);
		setResetError("");
		setResetSuccess("");
		setResetEmail("");
	};

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="glass-strong p-6 sm:p-8 lg:p-10 rounded-3xl shadow-2xl w-full max-w-md">
				<div className="text-center mb-6 sm:mb-8">
					<h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3c79bb] mb-2 drop-shadow-lg">PW Connect</h1>
					<p className="text-gray-900 text-sm sm:text-base font-medium">Team Communication & Training</p>
				</div>

				{serverStatus === "disconnected" && (
					<div className="glass bg-yellow-100/50 border border-yellow-300/50 text-yellow-900 px-4 py-3 rounded-xl mb-4 backdrop-blur-md">
						<p className="font-semibold text-sm sm:text-base">Cannot connect to server</p>
						<p className="text-xs sm:text-sm mt-1">
							Make sure the backend server is running on port 5001.
							<br />
							Run: <code className="bg-yellow-200/50 px-1.5 py-0.5 rounded text-xs">cd server && npm start</code>
						</p>
					</div>
				)}

				{/* ──── Login Form ──── */}
				{!showForgotPassword && (
					<>
						<form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
							{error && (
								<div className="glass bg-red-100/50 border border-red-300/50 text-red-800 px-4 py-3 rounded-xl backdrop-blur-md">
									<p className="text-sm sm:text-base">{error}</p>
								</div>
							)}

							<div>
								<label htmlFor="username" className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
									Username or email
								</label>
								<input
									id="username"
									type="text"
									autoComplete="username"
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									required
									className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium"
									placeholder="Enter your username or email"
								/>
							</div>

							<div>
								<label htmlFor="password" className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
									Password
								</label>
								<input
									id="password"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium"
									placeholder="Enter your password"
								/>
							</div>

							<button
								type="submit"
								disabled={loading}
								className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 px-4 rounded-xl hover:bg-[#306196] focus:outline-none focus:ring-2 focus:ring-[#3c79bb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold text-sm sm:text-base"
							>
								{loading ? "Logging in..." : "Login"}
							</button>
						</form>

						<div className="mt-4 text-center">
							<button
								onClick={() => setShowForgotPassword(true)}
								className="text-[#3c79bb] hover:text-[#306196] text-sm sm:text-base font-semibold hover:underline transition-all duration-200"
							>
								Forgot your password?
							</button>
						</div>
					</>
				)}

				{/* ──── Forgot Password — Email Request ──── */}
				{showForgotPassword && (
					<>
						<button
							onClick={handleBackToLogin}
							className="flex items-center gap-1.5 text-[#3c79bb] hover:text-[#306196] text-sm font-semibold mb-4 hover:underline transition-all duration-200"
						>
							<FiArrowLeft className="text-base" />
							Back to Login
						</button>

						<h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Reset Password</h2>
						<p className="text-xs sm:text-sm text-gray-700 font-medium mb-4 sm:mb-6">
							Enter your email address and we'll send you a link to reset your password.
						</p>

						{resetSuccess && (
							<div className="glass bg-green-100/50 border border-green-300/50 text-green-800 px-4 py-3 rounded-xl mb-4 backdrop-blur-md flex items-start gap-2">
								<FiCheckCircle className="text-lg flex-shrink-0 mt-0.5" />
								<div>
									<p className="text-sm sm:text-base font-semibold">{resetSuccess}</p>
									<p className="text-xs text-green-700 mt-1">Check your inbox (and spam folder) for the reset link.</p>
									<button
										onClick={handleBackToLogin}
										className="text-green-900 hover:text-green-700 text-sm font-semibold mt-2 hover:underline"
									>
										Back to Login
									</button>
								</div>
							</div>
						)}

						{!resetSuccess && (
							<form onSubmit={handleRequestReset} className="space-y-4">
								{resetError && (
									<div className="glass bg-red-100/50 border border-red-300/50 text-red-800 px-4 py-3 rounded-xl backdrop-blur-md">
										<p className="text-sm sm:text-base">{resetError}</p>
									</div>
								)}

								<div>
									<label htmlFor="reset-email" className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
										Email Address
									</label>
									<div className="relative">
										<FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
										<input
											id="reset-email"
											type="email"
											value={resetEmail}
											onChange={(e) => setResetEmail(e.target.value)}
											required
											className="w-full pl-10 pr-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium"
											placeholder="Enter your email address"
										/>
									</div>
								</div>

								<button
									type="submit"
									disabled={resetLoading}
									className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 px-4 rounded-xl hover:bg-[#306196] focus:outline-none focus:ring-2 focus:ring-[#3c79bb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold text-sm sm:text-base flex items-center justify-center gap-2"
								>
									{resetLoading ? (
										"Sending..."
									) : (
										<>
											<FiMail className="text-base" />
											Send Reset Link
										</>
									)}
								</button>
							</form>
						)}
					</>
				)}
			</div>
		</div>
	);
}

export default Login;
